/** @jsxImportSource @emotion/react */
import PropTypes from "prop-types";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import VideoPlayer from "../../../components/VideoPlayer";
import PdfViewer from "./PdfViewer";

const DrawerOfferCard = ({
  currentCard,
  currentStore,
  mailList,
  currentIndex,
  mailHTML,
  redeemedUuids,
  seconds,
  isPlainImage,
  classes,
  onClose,
  onNext,
  onPrev,
}) => {
  const isPaid = currentCard?.type === "paid";
  const isUnseen =
    currentCard?.is_seen === "false" && !redeemedUuids.has(currentCard?.uuid);
  const isSeen =
    currentCard?.is_seen === "true" || redeemedUuids.has(currentCard?.uuid);
  const hasVideo = !!currentCard?.video_url;
  const isHTML = currentCard?.mime_type === "text/html";
  const isPDF = currentCard?.mime_type === "application/pdf";
  const hasNonImageMedia = hasVideo || isHTML || isPDF;
  const isRedeeming = isPaid && isUnseen && seconds > 0;

  const renderMedia = () => {
    if (hasVideo) {
      return (
        <div
          style={{
            minWidth: "580px",
            minHeight: "340px",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          }}
        >
          <VideoPlayer
            url={currentCard?.video_url}
            width="100%"
            height="360px"
          />
        </div>
      );
    }
    if (isHTML) {
      return (
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            margin: "0 auto",
            padding: "16px",
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}
          dangerouslySetInnerHTML={{ __html: mailHTML }}
        />
      );
    }
    if (isPDF) {
      return <PdfViewer fileUrl={currentCard?.image_url} />;
    }
    if (currentCard?.image_url) {
      return (
        <img
          src={currentCard?.image_url}
          alt={currentCard?.subject || "Offer"}
        />
      );
    }
    return (
      <span style={{ color: "#999", padding: "40px" }}>No File Available</span>
    );
  };

  const renderContent = () => (
    <>
      <div css={classes.offerModalStoreRow}>
        {currentCard?.thumbnail_url && (
          <img
            css={classes.offerModalStoreLogo}
            src={currentCard?.thumbnail_url}
            alt={currentCard?.brand_name || "Store"}
          />
        )}
        <div css={classes.offerModalStoreInfo}>
          <div css={classes.offerModalStoreName}>
            {currentStore?.name || ""}
          </div>
          <h5 css={classes.offerModalSubject}>{currentCard?.subject || ""}</h5>
          <div css={classes.offerModalStoreBadges}>
            <div
              css={[
                classes.offerModalType,
                isPaid
                  ? classes.offerModalTypePaid
                  : classes.offerModalTypeRegular,
              ]}
            >
              {isPaid ? "💰 Paid Mail" : "📬 Regular Mail"}
            </div>
            {isPaid && currentCard?.amount > 0 && (
              <div css={classes.offerModalAmount}>
                <StarRoundedIcon style={{ fontSize: "16px" }} />
                {currentCard?.amount} Points
              </div>
            )}
            {isPaid && isSeen && (
              <div css={classes.offerModalRedeemBadge}>
                <CheckCircleIcon style={{ fontSize: "16px" }} />
                Redeemed
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        css={classes.offerModalMessage}
        dangerouslySetInnerHTML={{ __html: currentCard?.message || "" }}
      />
    </>
  );

  return (
    <div
      css={classes.offerModalOverlay}
      onClick={() => {
        if (!isRedeeming) onClose();
      }}
    >
      {!isRedeeming && (
        <button
          css={classes.offerModalBack}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <ArrowBackIosIcon /> Back
        </button>
      )}

      {!isRedeeming && (
        <button
          css={classes.offerModalClose}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </button>
      )}

      {!isRedeeming && mailList?.length > 1 && currentIndex > 0 && (
        <button
          css={[
            classes.offerModalNavLeft,
            isPDF && classes.offerModalNavLeftPdf,
          ]}
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
        >
          <ArrowBackIosIcon />
        </button>
      )}

      {isPaid && isUnseen && (
        <div
          css={classes.offerModalRedeemBar}
          onClick={(e) => e.stopPropagation()}
        >
          <AccessTimeIcon css={classes.offerModalTimerIcon} />
          {seconds > 0 ? (
            <>
              <span css={classes.offerModalTimerText}>Redeeming in</span>
              <span css={classes.offerModalTimerCount}>{seconds}s</span>
            </>
          ) : (
            <>
              <CheckCircleIcon style={{ color: "#2e7d32", fontSize: "22px" }} />
              <span
                css={classes.offerModalTimerText}
                style={{ color: "#2e7d32" }}
              >
                Redeemed!
              </span>
            </>
          )}
        </div>
      )}

      <div
        css={[classes.offerModalCard, isPDF && classes.offerModalCardPdf]}
        onClick={(e) => e.stopPropagation()}
      >
        {(isPlainImage && !hasNonImageMedia) || isPDF ? (
          <div css={classes.offerModalHorizontal}>
            <div css={classes.offerModalMediaLeft}>{renderMedia()}</div>
            <div css={classes.offerModalContentRight}>{renderContent()}</div>
          </div>
        ) : (
          <div css={classes.offerModalVertical}>
            <div css={classes.offerModalMediaTop}>{renderMedia()}</div>
            <div css={classes.offerModalContentBelow}>{renderContent()}</div>
          </div>
        )}
      </div>

      {!isRedeeming &&
        mailList?.length > 1 &&
        currentIndex < mailList.length - 1 && (
          <button
            css={[
              classes.offerModalNavRight,
              isPDF && classes.offerModalNavRightPdf,
            ]}
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            <ArrowForwardIosIcon />
          </button>
        )}
    </div>
  );
};

DrawerOfferCard.propTypes = {
  currentCard: PropTypes.object,
  currentStore: PropTypes.object,
  mailList: PropTypes.array.isRequired,
  currentIndex: PropTypes.number.isRequired,
  mailHTML: PropTypes.string,
  redeemedUuids: PropTypes.instanceOf(Set).isRequired,
  seconds: PropTypes.number.isRequired,
  isPlainImage: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
};

export default DrawerOfferCard;
